import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiStar, FiGift, FiCheckCircle, FiInfo, FiExternalLink, FiChevronLeft, FiChevronRight, FiThumbsUp, FiThumbsDown, FiLink, FiCheck } from 'react-icons/fi';
import { trackAffiliateClick } from '../services/api';
import './CasinoModal.css';

// standalone = rendered as its own page under /bonus/<kuerzel>/ instead of as an overlay
const CasinoModal = ({ casino, onClose, showRatingsAndProcons, standalone = false }) => {
  const [currentSection, setCurrentSection] = useState('description');
  const [linkCopied, setLinkCopied] = useState(false);

  const shareUrl = casino.detailUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const handleShare = async () => {
    if (!shareUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({ title: casino.name, url: shareUrl });
        return;
      } catch (error) {
        // User dismissed the share sheet – fall through to copying
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      console.error('Could not copy link:', error);
    }
  };

  const handlePrevSection = () => {
    setCurrentSection(prev => prev === 'description' ? 'procons' : 'description');
  };

  const handleNextSection = () => {
    setCurrentSection(prev => prev === 'description' ? 'procons' : 'description');
  };
  const getBonusTypeLabel = (type) => {
    const labels = {
      welcome: 'Welcome Bonus',
      deposit: 'Deposit Bonus',
      freespins: 'Free Spins',
      cashback: 'Cashback',
      nodeposit: 'No Deposit'
    };
    return labels[type] || type;
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FiStar
        key={i}
        className={i < rating ? 'star filled' : 'star'}
      />
    ));
  };

  // Auf den eigenen Angebotsseiten sitzt der CTA direkt unter dem Bonus-Kasten,
  // im Overlay-Modal bleibt er wie gewohnt unten.
  const ctaButton = (
    <motion.a
      href={casino.affiliateLink || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="modal-cta-button"
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => {
        trackAffiliateClick(casino.id);
      }}
    >
      Claim Bonus Now
      <FiExternalLink />
    </motion.a>
  );

  return (
    <AnimatePresence>
      <motion.div
        className={standalone ? 'modal-overlay modal-overlay-standalone' : 'modal-overlay'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={standalone ? undefined : onClose}
      >
        <motion.div
          className="modal-content"
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Share Button – copies the offer's own URL */}
          {shareUrl && (
            <motion.button
              className={linkCopied ? 'share-button copied' : 'share-button'}
              onClick={handleShare}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={linkCopied ? 'Link kopiert!' : 'Link zu diesem Angebot teilen'}
              aria-label="Link zu diesem Angebot teilen"
            >
              {linkCopied ? <FiCheck /> : <FiLink />}
            </motion.button>
          )}

          {/* Close Button */}
          <motion.button
            className="close-button"
            onClick={onClose}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            title={standalone ? 'Zur Übersicht' : 'Schließen'}
            aria-label={standalone ? 'Zur Übersicht' : 'Schließen'}
          >
            <FiX />
          </motion.button>

          {/* Header */}
          <div className="modal-header">
            <div className="modal-logo">
              {casino.logo ? (
                <img src={casino.logo} alt={casino.name} />
              ) : (
                <div className="logo-placeholder">🎰</div>
              )}
            </div>
            <h2>{casino.name}</h2>
          </div>

          {/* Bonus Highlight */}
          <motion.div
            className="modal-bonus-highlight"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="modal-bonus-grid">
              <div className="modal-bonus-stat">
                <div className="modal-stat-value">{casino.bonusPercentage || 100}%</div>
                <div className="modal-stat-label">{casino.bonusStickyType?.toUpperCase() || 'WAGERFREE'}</div>
              </div>
              <div className="modal-bonus-stat">
                <div className="modal-stat-value">{casino.maxBonus || 1000}€</div>
                <div className="modal-stat-label">MAX BONUS</div>
              </div>
              <div className="modal-bonus-stat">
                <div className="modal-stat-value">{casino.wageringRequirement || 0}x</div>
                <div className="modal-stat-label">WAGER ({casino.wagerType || 'D'})</div>
              </div>
              <div className="modal-bonus-stat">
                <div className="modal-stat-value">{casino.maxBet || 5}€</div>
                <div className="modal-stat-label">MAXBET</div>
              </div>
            </div>
          </motion.div>

          {standalone && ctaButton}

          {/* Navigable Content Section */}
          <div className="modal-section navigable-section">
            <div className="section-nav-header">
              {showRatingsAndProcons && casino.pros && casino.pros.length > 0 ? (
                <>
                  <button
                    className="nav-arrow nav-arrow-left"
                    onClick={handlePrevSection}
                  >
                    <FiChevronLeft />
                  </button>

                  <h3 className="section-title">
                    {currentSection === 'description' ? (
                      <>
                        <FiInfo /> Description & Highlights
                      </>
                    ) : (
                      <>
                        <FiThumbsUp /> Pros & Cons
                      </>
                    )}
                  </h3>

                  <button
                    className="nav-arrow nav-arrow-right"
                    onClick={handleNextSection}
                  >
                    <FiChevronRight />
                  </button>
                </>
              ) : (
                <>
                  <div className="nav-arrow-placeholder"></div>
                  <h3 className="section-title">
                    <FiInfo /> Description & Highlights
                  </h3>
                  <div className="nav-arrow-placeholder"></div>
                </>
              )}
            </div>

            <AnimatePresence mode="wait">
              {currentSection === 'description' && (
                <motion.div
                  key="description"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.3 }}
                  className="section-content"
                >
                  {casino.description && (
                    <div className="description-block">
                      <p>{casino.description}</p>
                    </div>
                  )}

                  {casino.features && casino.features.length > 0 && (
                    <div className="highlights-block">
                      <h4><FiCheckCircle /> Highlights</h4>
                      <div className="features-grid">
                        {casino.features.map((feature, index) => (
                          <div key={index} className="feature-item">
                            <FiCheckCircle className="check-icon" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}

              {currentSection === 'procons' && showRatingsAndProcons && (
                <motion.div
                  key="procons"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="section-content procons-content"
                >
                  <div className="procons-grid">
                    {casino.pros && casino.pros.length > 0 && (
                      <div className="pros-block">
                        <h4><FiThumbsUp /> Pros</h4>
                        <ul className="procons-list">
                          {casino.pros.map((pro, index) => (
                            <li key={index} className="pro-item">
                              <FiThumbsUp className="pro-icon" />
                              <span>{pro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {casino.cons && casino.cons.length > 0 && (
                      <div className="cons-block">
                        <h4><FiThumbsDown /> Cons</h4>
                        <ul className="procons-list">
                          {casino.cons.map((con, index) => (
                            <li key={index} className="con-item">
                              <FiThumbsDown className="con-icon" />
                              <span>{con}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* License & Payment Methods */}
          {(casino.licenseLogo || (casino.paymentMethods && casino.paymentMethods.length > 0)) && (
            <div className="modal-section license-payment-section">
              <div className="license-payment-grid">
                {casino.licenseLogo && (
                  <div className="license-container">
                    <h4>License</h4>
                    <div className="license-logo">
                      <img src={casino.licenseLogo} alt="License" />
                    </div>
                  </div>
                )}
                {casino.paymentMethods && casino.paymentMethods.length > 0 && (
                  <div className="payment-container">
                    <h4>Deposit Methods</h4>
                    <div className="payment-logos">
                      {casino.paymentMethods.map((method, index) => (
                        <img key={index} src={method} alt={`Payment method ${index + 1}`} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="modal-section terms-section">
            <h3>Terms & Conditions</h3>
            <div className="terms-grid">
              <div className="term-item">
                <strong>Bonus Percentage:</strong>
                <span>{casino.bonusPercentage || 100}%</span>
              </div>
              <div className="term-item">
                <strong>Bonus Type:</strong>
                <span>{casino.bonusStickyType?.toUpperCase() || 'WAGERFREE'}</span>
              </div>
              <div className="term-item">
                <strong>Max Bonus:</strong>
                <span>${casino.maxBonus || 1000}</span>
              </div>
              <div className="term-item">
                <strong>Wagering:</strong>
                <span>{casino.wageringRequirement || 0}x</span>
              </div>
              <div className="term-item">
                <strong>Max Bet:</strong>
                <span>${casino.maxBet || 5}</span>
              </div>
              {casino.minDeposit && (
                <div className="term-item">
                  <strong>Min. Deposit:</strong>
                  <span>${casino.minDeposit}</span>
                </div>
              )}
            </div>
          </div>

          {/* CTA Button (im Standalone-Modus weiter oben) */}
          {!standalone && ctaButton}

          {/* Legal Notice */}
          <div className="modal-legal">
            <small>18+ | Terms & Conditions apply | Please gamble responsibly</small>
          </div>

          {/* Back to the overview when this offer has its own page */}
          {standalone && (
            <button className="modal-back-link" onClick={onClose}>
              <FiChevronLeft /> Alle Angebote ansehen
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CasinoModal;
